/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect } from 'react';
import { UIContextProvider } from 'contexts/UIContext';
import 'styles/globals.scss';
import { useRouter } from 'next/router';
import { StoreContextProvider } from 'contexts/StoreContext';
import getCurrency from 'lib/storeLocales/getCurrency';
import split from 'lib/storeLocales/split';
import { trackInWindow, trackEvents, getAlgoliaUserTokenFromGACookie } from 'hooks/tracking/track';
import GorgiasScript from 'components/scripts/gorgias';
import GoogleTagManagerScript from 'components/scripts/google-tag-manager';
import CookieProScript from 'components/scripts/cookie-pro';
import HighstreetAppMeta from 'components/highstreet-app-meta';
import Maintenance from 'components/pages/maintenance';
import ErrorBoundary from 'components/error-boundary';
import isProduction from 'lib/isProduction';
import HighstreetAppBanner from 'components/scripts/highstreet-app-banner';
import { trackExperiment } from 'lib/server-experiments';
import Script from 'next/script';
import { useCustomer } from 'hooks';
import stripShopifyGid from 'lib/utils/stripShopifyGid';
import { SessionProvider } from 'next-auth/react';
import GlobalStructuredData from 'components/global-structured-data';

function MyApp({ Component, pageProps, router, session }) {
  if (process.env.MAINTENANCE_MODE) return <Maintenance />;

  const { locale: storeLocale } = useRouter();
  const [store, locale] = split(storeLocale);
  const { isApp = false } = pageProps;
  const { customer } = useCustomer();

  useEffect(() => {
    // Chrome css bug
    document.body.classList?.remove('loading');
  }, []);

  // track algoliaUserToken once GTM is initialized. Doing an interval here to check if GTM is initialized

  useEffect(() => {
    const totalRetry = 5;
    const intervalDelay = 2000;
    let retries = totalRetry;

    const intervalId = setInterval(() => {
      if (window.hasGTMInit) {
        const userToken = customer?.id
          ? stripShopifyGid(customer?.id)
          : getAlgoliaUserTokenFromGACookie();

        trackInWindow({
          algoliaUserToken: userToken,
        });

        clearInterval(intervalId); // Stop further retries once tracking is done
      } else if (retries <= 0) {
        clearInterval(intervalId); // Stop if retries are exhausted
      }

      retries -= 1;
    }, intervalDelay);

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, [customer?.id]);

  useEffect(() => {
    const onRouteComplete = url => {
      trackInWindow({
        event: trackEvents.routeChange,
        page_path: url,
      });

      trackExperiment();
    };

    if (!isApp) router.events.on('routeChangeComplete', onRouteComplete);

    return () => {
      router.events.off('routeChangeComplete', onRouteComplete);
    };
  }, [isApp, router]);

  return (
    <ErrorBoundary>
      <SessionProvider session={session}>
        <StoreContextProvider
          locale={locale}
          store={store}
          preview={!!pageProps.previewData}
          currency={getCurrency(store)}
        >
          <HighstreetAppMeta />
          <UIContextProvider
            isApp={isApp}
            state={{ displayUspBanner: Boolean(pageProps?.uspBanner) }}
            headerTransparencyDesktop={pageProps?.page?.headerTransparent || false}
          >
            {!isApp && (
              <>
                <CookieProScript />
                {/* https://support.newstore.com/s/article/How-to-implement-our-smartbanner */}
                <HighstreetAppBanner />
                {isProduction && <GoogleTagManagerScript />}
                {/* TODO: Rescan cookies on CookiePro and add the correctly configured snippet here (after any cookie changes) */}
                <GorgiasScript />

                <GlobalStructuredData />

                <Script
                  id="vwoCode"
                  strategy="beforeInteractive"
                  dangerouslySetInnerHTML={{
                    __html: `window._vwo_code || (function() {
                      var account_id=734704,
                      version=2.0,
                      settings_tolerance=2000,
                      hide_element='body',
                      hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
                      /* DO NOT EDIT BELOW THIS LINE */
                      f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(){if(!f){f=true;var e=d.getElementById('_vis_opt_path_hides');if(e)e.parentNode.removeChild(e)}},finished:function(){return f},load:function(e){var t=this.getSettings(),n=d.createElement('script'),i=this;if(t){n.textContent=t;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);i.load(e)}}else{n.fetchPriority='high';n.src=e;n.type='text/javascript';n.onerror=function(){w._vwo_code.finish()};d.getElementsByTagName('head')[0].appendChild(n)}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){w._vwo_code.finish();stT.removeItem(cK)},e);var t=d.currentScript,n=d.createElement('style'),i=this.hide_element(),r=t&&!t.async&&i?i+this.hide_element_style():'',c=d.getElementsByTagName('head')[0];n.setAttribute('id','_vis_opt_path_hides');v&&n.setAttribute('nonce',v.nonce);n.setAttribute('type','text/css');if(n.styleSheet)n.styleSheet.cssText=r;else n.appendChild(d.createTextNode(r));c.appendChild(n);this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version)}};w._vwo_code=code;code.init();})();(function(){var i=window;function t(){if(i._vwo_code){var e=t.hidingStyle=document.getElementById('_vis_opt_path_hides')||t.hidingStyle;if(!i._vwo_code.finished()&&!_vwo_code.libExecuted&&(!i.VWO||!VWO.dNR)){if(!document.getElementById('_vis_opt_path_hides')){document.getElementsByTagName('head')[0].appendChild(e)}requestAnimationFrame(t)}}}t()})();
                    `,
                  }}
                />
              </>
            )}
            <Component
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...pageProps}
            />
          </UIContextProvider>
        </StoreContextProvider>
      </SessionProvider>
    </ErrorBoundary>
  );
}

export default MyApp;
